import { useCallback, useEffect, useState } from 'react'
import {
  NOTE_COURSES_CLIENT_API, PHRASE_OUVRIR_ACCES_TEST, URL_PORTAIL_DEVELOPPEURS, classeAccesApi, libelleDrapeauxApi,
  messageAccesApi, resumeAccesClientApi, statutOnboardingApi, texteAccesClientApi, validerClientApi,
  type CleApi, type FormulaireClientApi, type LigneClientApi, type ResultatClientApi,
} from '../api/accesApi'
import { ChampTelephone, afficherTelephone, useTelephone } from '../api/commun'
import { dateHeure, dateLongue } from '../api/format'
import { MESSAGE_COMPTE_EXISTANT_FONCTION } from '../api/identifiants'
import { supabase } from '../api/supabase'
import type { Source } from '../api/types'
import { Badge } from './Badges'
import { Icone } from './Icone'
import { Modale } from './Modale'
import {
  InterrupteursAcces, JournalAcces, ModaleChangementAcces, ModaleRevocation, TableCles, useListe,
  type Chargement, type Drapeaux,
} from './SectionAccesApi'

type Etat =
  | { statut: 'chargement' }
  | { statut: 'erreur'; message: string }
  | { statut: 'pret'; lignes: LigneClientApi[] }

/**
 * Onglet « Clients API » — le cas (a) : un client API SANS flotte est un client OPÉRÉ de l'organisation opératrice
 * (notre flotte livre ses courses). Son compte de connexion est le profil relié au client ; ses drapeaux d'accès
 * (test, production) sont posés ici, par un administrateur, avec une raison journalisée. Si la migration 0048 n'est
 * pas encore poussée, la lecture échoue : on le dit discrètement, la console reste utilisable.
 */
export function ClientsApi({
  source,
  operatorOrgId,
  notifier,
}: {
  source: Source
  /** Organisation opératrice, lue dans la vue d'ensemble ; null tant qu'elle n'est pas connue. */
  operatorOrgId: string | null
  notifier: (m: string) => void
}) {
  const [etat, setEtat] = useState<Etat>({ statut: 'chargement' })
  const [selection, setSelection] = useState<string | null>(null)
  const [creation, setCreation] = useState(false)

  /** `silencieux` : garde le tableau et la fiche en place pendant le rechargement (après une écriture). Ne lève jamais. */
  const charger = useCallback(async (silencieux: boolean) => {
    if (!silencieux) setEtat({ statut: 'chargement' })
    try {
      setEtat({ statut: 'pret', lignes: await source.clientsApi() })
    } catch (e) {
      const message = messageAccesApi(e)
      if (silencieux) notifier(`Rechargement impossible : ${message}`)
      else setEtat({ statut: 'erreur', message })
    }
  }, [source, notifier])

  useEffect(() => { void charger(false) }, [charger])

  const lignes = etat.statut === 'pret' ? etat.lignes : []
  const fiche = lignes.find((l) => l.client_id === selection) ?? null

  return (
    <>
      <section className="carte">
        <div className="carte-tete">
          <div>
            <h2>Clients API</h2>
            <p className="sous">
              Clients opérés qui intègrent notre API : {etat.statut === 'pret' ? `${lignes.length} client(s)` : 'chargement…'}
            </p>
          </div>
          <div className="actions">
            <button
              type="button" className="btn btn-plein btn-sm" onClick={() => setCreation(true)}
              disabled={!operatorOrgId}
              title={operatorOrgId ? undefined : "L'organisation opératrice n'est pas encore chargée."}
            >
              <Icone nom="plus" /> Créer un client API
            </button>
          </div>
        </div>

        {etat.statut === 'chargement' && <div className="carte-corps" aria-busy="true" aria-label="Chargement des clients API"><div className="sk" /><div className="sk court" /></div>}
        {etat.statut === 'erreur' && (
          <div className="carte-corps">
            <p className="encart encart-alerte" role="status">
              <Icone nom="alert" /> Les clients API ne sont pas lisibles pour le moment : {etat.message}{' '}
              <button type="button" className="lien" onClick={() => void charger(false)}>Réessayer</button>
            </p>
          </div>
        )}
        {etat.statut === 'pret' && (
          <div className="defile">
            <table>
              <thead>
                <tr>
                  <th>Client</th><th>Onboarding</th><th>Compte relié</th><th>Accès API</th>
                  <th className="c">Clés actives (test / live)</th><th>Dernier usage</th><th><span className="sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {lignes.length === 0 ? (
                  <tr><td colSpan={7}><div className="vide">Aucun client API pour le moment. Créez-en un avec « Créer un client API ».</div></td></tr>
                ) : lignes.map((l) => {
                  const ob = statutOnboardingApi(l.onboarding_status)
                  return (
                    <tr key={l.client_id}>
                      <td className="fort">{l.client_nom}</td>
                      <td><Badge classe={ob.classe}>{ob.libelle}</Badge></td>
                      <td>{l.comptes > 0 ? 'oui' : <span className="discret-t">non</span>}</td>
                      <td><Badge classe={classeAccesApi(l)}>{libelleDrapeauxApi(l)}</Badge></td>
                      <td className="c n">{l.cles_test_actives} / {l.cles_live_actives}</td>
                      <td className="discret-t">{l.derniere_utilisation ? dateHeure(l.derniere_utilisation) : 'jamais'}</td>
                      <td>
                        <button type="button" className="btn btn-doux btn-sm" onClick={() => setSelection(l.client_id)} aria-label={`Ouvrir la fiche de ${l.client_nom}`}>
                          Fiche
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="note">{NOTE_COURSES_CLIENT_API}</p>
      </section>

      {fiche && (
        <FicheClientApi
          ligne={fiche}
          source={source}
          surChange={() => charger(true)}
          surFermer={() => setSelection(null)}
          notifier={notifier}
        />
      )}

      {creation && operatorOrgId && (
        <ModaleClientApi
          operatorOrgId={operatorOrgId}
          source={source}
          surFermer={() => setCreation(false)}
          surFait={(clientId) => { void charger(true); setSelection(clientId) }}
          notifier={notifier}
        />
      )}
    </>
  )
}

/**
 * Fiche d'un client API : compte développeur, accès (test / production, raison obligatoire), clés (révocation avec
 * raison) et journal. Mêmes briques que « Accès API » d'une organisation, mais sur le client (`p_client`).
 */
function FicheClientApi({
  ligne,
  source,
  surChange,
  surFermer,
  notifier,
}: {
  ligne: LigneClientApi
  source: Source
  /** Recharge le tableau des clients API. Ne lève jamais. */
  surChange: () => Promise<void>
  surFermer: () => void
  notifier: (m: string) => void
}) {
  const [version, setVersion] = useState(0)
  const [changement, setChangement] = useState<Drapeaux | null>(null)
  const [aRevoquer, setARevoquer] = useState<CleApi | null>(null)
  const [cles, rechargerCles] = useListe(() => source.clesApi(ligne.org_id, ligne.client_id), [source, ligne.org_id, ligne.client_id, version])
  const [journal, rechargerJournal] = useListe(() => source.journalAccesApi(ligne.org_id, ligne.client_id), [source, ligne.org_id, ligne.client_id, version])
  const [comptes, rechargerComptes] = useListe(() => source.comptesClientApi(ligne.client_id), [source, ligne.client_id, version])

  const nom = ligne.client_nom
  const ob = statutOnboardingApi(ligne.onboarding_status)
  const actuel: Drapeaux = { sandbox: ligne.api_sandbox, production: ligne.api_production }

  async function apresEcriture(message: string) {
    setVersion((v) => v + 1)
    notifier(message)
    await surChange()
  }

  return (
    <Modale variante="tiroir" titre={nom} sousTitre="Client API · offre opérée" surFermer={surFermer}>
      <div className="fiche-badges">
        <Badge classe={ob.classe}>{`Onboarding : ${ob.libelle}`}</Badge>
        <Badge classe={classeAccesApi(ligne)}>{libelleDrapeauxApi(ligne)}</Badge>
      </div>

      <p className="encart" style={{ marginTop: 14 }}><Icone nom="truck" /> {NOTE_COURSES_CLIENT_API}</p>

      <h3 className="fiche-titre">Compte développeur</h3>
      {comptes.etat === 'chargement' && <div className="sk" aria-busy="true" aria-label="Chargement du compte" />}
      {comptes.etat === 'erreur' && (
        <p className="encart encart-alerte" role="alert">
          <Icone nom="alert" /> {comptes.message}{' '}
          <button type="button" className="lien" onClick={rechargerComptes}>Réessayer</button>
        </p>
      )}
      {comptes.etat === 'pret' && comptes.lignes.length === 0 && (
        <p className="encart encart-alerte" role="status">
          <Icone nom="alert" /> Aucun compte de connexion n'est relié à ce client. Utilisez « Créer un client API » puis
          « Client opéré existant » pour lui en créer un.
        </p>
      )}
      {comptes.etat === 'pret' && comptes.lignes.map((c) => (
        <dl className="liste-def" key={c.profile_id}>
          <div><dt>Nom</dt><dd>{c.nom ?? '—'}</dd></div>
          <div><dt>E-mail</dt><dd>{c.email ?? '—'}</dd></div>
          <div><dt>Numéro WhatsApp</dt><dd>{c.phone ? afficherTelephone(c.phone) : '—'}</dd></div>
          <div><dt>Compte créé le</dt><dd>{dateLongue(c.created_at)}</dd></div>
        </dl>
      ))}

      <h3 className="fiche-titre">Accès API</h3>
      <dl className="liste-def">
        <div><dt>Accès actuel</dt><dd>{libelleDrapeauxApi(ligne)}</dd></div>
        <div><dt>Clés actives (test / production)</dt><dd className="n">{ligne.cles_test_actives} / {ligne.cles_live_actives}</dd></div>
        <div><dt>Dernière utilisation</dt><dd>{ligne.derniere_utilisation ? dateHeure(ligne.derniere_utilisation) : '—'}</dd></div>
        <div><dt>Dernier changement</dt><dd>{ligne.dernier_changement ? dateHeure(ligne.dernier_changement) : '—'}</dd></div>
      </dl>
      <InterrupteursAcces actuel={actuel} surChoix={setChangement} />

      <TableCles etat={cles} recharger={rechargerCles} surRevoquer={setARevoquer} vide="Aucune clé créée pour ce client." />
      <JournalAcces etat={journal} recharger={rechargerJournal} />

      {changement && (
        <ModaleChangementAcces
          nom={nom}
          avant={actuel}
          apres={changement}
          enregistrer={(m) => source.definirAccesApiClient({ client: ligne.client_id, ...m })}
          surFermer={() => setChangement(null)}
          surFait={() => apresEcriture(`Accès API de « ${nom} » mis à jour`)}
        />
      )}
      {aRevoquer && (
        <ModaleRevocation
          cle={aRevoquer}
          nom={nom}
          revoquer={(note) => source.revoquerCleApi(aRevoquer.id, note)}
          surFermer={() => setARevoquer(null)}
          surFait={() => apresEcriture(`Clé « ${aRevoquer.label} » révoquée`)}
        />
      )}
    </Modale>
  )
}

const MODES: { code: FormulaireClientApi['mode']; nom: string; sous: string }[] = [
  { code: 'nouveau', nom: 'Nouveau client', sous: 'fiche créée chez nous' },
  { code: 'existant', nom: 'Client opéré existant', sous: 'déjà livré par notre flotte' },
]

/**
 * « Créer un client API » : edge function `creer-client-api` — un compte développeur relié à un NOUVEAU client opéré
 * ou à un client PME existant de l'opératrice. N'ouvre AUCUN accès (c'est la fiche du client, journalisée, qui le fait).
 * Les accès sont affichés UNE SEULE FOIS : le mot de passe temporaire n'est stocké nulle part.
 */
function ModaleClientApi({
  operatorOrgId,
  source,
  surFermer,
  surFait,
  notifier,
}: {
  operatorOrgId: string
  source: Source
  surFermer: () => void
  /** Reçoit l'identifiant du client pour ouvrir sa fiche. */
  surFait: (clientId: string) => void
  notifier: (m: string) => void
}) {
  const [fBase, setFBase] = useState<Omit<FormulaireClientApi, 'telephone'>>({ mode: 'nouveau', name: '', clientId: '', contact_name: '', email: '' })
  const [erreur, setErreur] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [resultat, setResultat] = useState<ResultatClientApi | null>(null)

  const tel = useTelephone(supabase)
  const f: FormulaireClientApi = { ...fBase, telephone: tel.valeur }
  const maj = <K extends keyof Omit<FormulaireClientApi, 'telephone'>>(k: K, v: FormulaireClientApi[K]) => setFBase((x) => ({ ...x, [k]: v }))

  const [existants]: [Chargement<{ id: string; name: string; profile_id: string | null }>, () => void] = useListe(
    () => (fBase.mode === 'existant' ? source.clientsOperesPme(operatorOrgId) : Promise.resolve([])),
    [source, operatorOrgId, fBase.mode],
  )

  async function creer() {
    if (enCours) return
    const refus = validerClientApi(f)
    if (refus) { setErreur(refus); return }
    setEnCours(true)
    setErreur('')
    try {
      const r = await source.creerClientApi(f)
      setResultat(r)
      notifier(r.clientCree ? `Client API « ${f.name.trim()} » créé` : 'Compte développeur rattaché au client')
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création refusée : réessayez.')
    } finally {
      setEnCours(false)
    }
  }

  async function copier() {
    if (!resultat) return
    try {
      await navigator.clipboard.writeText(texteAccesClientApi(resultat))
      notifier('Accès copiés')
    } catch {
      notifier('Copie impossible')
    }
  }

  if (resultat) {
    const resume = resumeAccesClientApi(resultat)
    const terminer = () => { surFait(resultat.clientId); surFermer() }
    return (
      <Modale
        titre={resultat.clientCree ? 'Client API créé' : 'Compte développeur rattaché'}
        surFermer={terminer}
        pied={<button type="button" className="btn btn-plein" onClick={terminer} data-focus-initial>Terminé</button>}
      >
        {resume.secret
          ? <p className="encart"><Icone nom="alert" /> Transmettez ces accès au développeur. <b>Ils ne seront plus affichés.</b></p>
          : <p className="encart"><Icone nom="alert" /> {resultat.connexion === 'whatsapp' ? resume.phrase : MESSAGE_COMPTE_EXISTANT_FONCTION}</p>}
        <dl className="liste-def">
          <div><dt>Espace développeur</dt><dd>{URL_PORTAIL_DEVELOPPEURS.replace('https://', '')}</dd></div>
          {resultat.email && <div><dt>E-mail</dt><dd>{resultat.email}</dd></div>}
          {resultat.phone && <div><dt>Numéro WhatsApp</dt><dd>{afficherTelephone(resultat.phone)}</dd></div>}
          {resume.secret && <div><dt>Mot de passe temporaire</dt><dd className="n">{resume.secret}</dd></div>}
        </dl>
        <p className="encart"><Icone nom="lock" /> {PHRASE_OUVRIR_ACCES_TEST} Connexion : {URL_PORTAIL_DEVELOPPEURS}</p>
        <button type="button" className="btn btn-doux" onClick={() => void copier()}><Icone nom="copy" /> Copier les accès</button>
      </Modale>
    )
  }

  const existant = fBase.mode === 'existant'
  return (
    <Modale
      titre="Créer un client API"
      sousTitre="Compte développeur d'un client opéré"
      surFermer={surFermer}
      pied={
        <>
          <button type="button" className="btn btn-doux" onClick={surFermer} disabled={enCours}>Annuler</button>
          <button type="button" className="btn btn-plein" onClick={() => void creer()} disabled={enCours}>
            {enCours ? 'Création…' : 'Créer le compte développeur'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void creer() }} noValidate>
        <fieldset className="champ">
          <legend>Client</legend>
          <div className="choix deux">
            {MODES.map((m) => (
              <label key={m.code} className={`choix-item${fBase.mode === m.code ? ' on' : ''}`}>
                <input type="radio" name="mode-client-api" checked={fBase.mode === m.code} onChange={() => { maj('mode', m.code); setErreur('') }} />
                <span className="choix-nom">{m.nom}</span>
                <span className="choix-sous">{m.sous}</span>
              </label>
            ))}
          </div>
          <span className="aide">
            Le client API n'a pas de flotte : notre flotte livre ses courses (offre opérée). Aucun accès API n'est ouvert à la création.
          </span>
        </fieldset>

        {!existant ? (
          <div className="champ">
            <label htmlFor="ca-nom">Raison sociale <b aria-hidden="true">*</b></label>
            <input id="ca-nom" placeholder="Kala Paiements SARL" value={fBase.name} onChange={(e) => maj('name', e.target.value)} required />
          </div>
        ) : (
          <div className="champ">
            <label htmlFor="ca-existant">Client opéré <b aria-hidden="true">*</b></label>
            <select id="ca-existant" value={fBase.clientId} onChange={(e) => maj('clientId', e.target.value)} disabled={existants.etat !== 'pret'} required>
              <option value="">{existants.etat === 'chargement' ? 'Chargement…' : 'Choisir un client…'}</option>
              {existants.etat === 'pret' && existants.lignes.map((c) => (
                <option key={c.id} value={c.id} disabled={c.profile_id !== null}>
                  {c.name}{c.profile_id !== null ? ' — compte déjà relié' : ''}
                </option>
              ))}
            </select>
            {existants.etat === 'erreur' && <span className="erreur" role="alert">{existants.message}</span>}
            {existants.etat === 'pret' && existants.lignes.every((c) => c.profile_id !== null) && (
              <span className="aide">Aucun client PME sans compte relié : créez plutôt un nouveau client.</span>
            )}
          </div>
        )}

        <div className="ligne2">
          <div className="champ">
            <label htmlFor="ca-contact">Contact (développeur) <b aria-hidden="true">*</b></label>
            <input id="ca-contact" placeholder="Fatou Coulibaly" value={fBase.contact_name} onChange={(e) => maj('contact_name', e.target.value)} required />
          </div>
          <div className="champ">
            <label htmlFor="ca-mail">E-mail du contact</label>
            <input id="ca-mail" type="email" placeholder="dev@entreprise.ml" value={fBase.email} onChange={(e) => maj('email', e.target.value)} />
          </div>
        </div>
        <div className="champ">
          <ChampTelephone
            valeur={tel.valeur}
            surChangement={tel.setValeur}
            etiquette="Numéro WhatsApp du contact"
            montrerErreur={erreur !== ''}
            id="ca-tel"
          />
          <span className="aide">Au moins un des deux : e-mail ou numéro WhatsApp. Avec un e-mail, un contact sans compte reçoit un mot de passe temporaire ; avec un numéro seul, il se connecte avec un code reçu sur WhatsApp.</span>
        </div>
        <p className="erreur" role="alert" aria-live="assertive">{erreur}</p>
      </form>
    </Modale>
  )
}
